/* eslint-disable react-hooks/exhaustive-deps */
import {
  filmCinemasResponse,
  filmExperiencesResponse,
  movieDetailsResponse,
  useCheckMovieOffers,
  useGetMovieCinemas,
  useGetMovieCinemasFilter,
  useGetMovieDetailsBySlug,
  useGetMovieExperiencesFilter,
  useGetOfferDetails,
} from '@/services';
import { useDialog, useMovieDetailsState, useVerifiedOffer } from '../state';

import { selectedFilters } from '@/components/movie-details/filters/selected-filters';
import shallow from 'zustand/shallow';
import { useBankOfferParams } from './use-bank-offer-params';
import { useCinema } from './use-cinema';
import { useCity } from './use-city';
import { useClearBooking } from './use-clear-booking';
import { useEffect } from 'react';
import { useOfferHandler } from './use-offer-handler';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useUnipalOfferParams } from '@/hooks/use-unipal-offer-params';

interface useMovieDetailsProps {
  serverMovie: movieDetailsResponse;
  serverMovieCinemasFilter: filmCinemasResponse;
  serverMovieExperiencesFilter: filmExperiencesResponse;
}

export const useMovieDetails = ({
  serverMovie,
  serverMovieCinemasFilter,
  serverMovieExperiencesFilter,
}: useMovieDetailsProps) => {
  const queryClient = useQueryClient();
  const setDialog = useDialog(s => s.setDialog);
  const router = useRouter();
  const { cityId: browserCityId } = useCity();
  // || outside Number(): a malformed ?cityId= (e.g. 'null') must fall back to
  // the user's city instead of poisoning the whole derivation to NaN
  const cityId = Number(router.query.cityId) || browserCityId;
  const { selectedCinemas } = useCinema();
  const { clearBooking } = useClearBooking();

  const [
    movie,
    setMovie,
    offer,
    setOffer,
    showTimeDate,
    setShowTimeDate,
    firstEnabledDay,
    setFilmCinemas,
    selectedFilters,
    setSelectedFilters,
    selectedCinemasFilter,
    setSelectedCinemasFilter,
    selectedExperiencesFilter,
    setSelectedExperiencesFilter,
    cinemasParameters,
    setCinemasParameters,
    movieCinemasFilter,
    setMovieCinemasFilter,
    movieExperiencesFilter,
    setMovieExperiencesFilter,
    setIsFetchingCinemas,
    setIsFetchingExperiences,
    setIsSeeResultsDisabled,
    setShowMoreBtn,
    hasMoreCinemas,
    setHasMoreCinemas,
    isFilterMode,
    setIsFetching,
    setFirstEnabledDay,
    setIsFiltersDrawerOpen,
    setIsOfferApplied,
    setIsFilterMode,
    setIsThereAtLeastOneAvailableShowTime,
    setMovieHasOffers,
    setHasOfferError,
    setHasOfferLoading,
  ] = useMovieDetailsState(
    s => [
      s.movie,
      s.setMovie,
      s.offer,
      s.setOffer,
      s.showTimeDate,
      s.setShowTimeDate,
      s.firstEnabledDay,
      s.setFilmCinemas,
      s.selectedFilters,
      s.setSelectedFilters,
      s.selectedCinemasFilter,
      s.setSelectedCinemasFilter,
      s.selectedExperiencesFilter,
      s.setSelectedExperiencesFilter,
      s.cinemasParameters,
      s.setCinemasParameters,
      s.movieCinemasFilter,
      s.setMovieCinemasFilter,
      s.movieExperiencesFilter,
      s.setMovieExperiencesFilter,
      s.setIsFetchingCinemas,
      s.setIsFetchingExperiences,
      s.setIsSeeResultsDisabled,
      s.setShowMoreBtn,
      s.hasMoreCinemas,
      s.setHasMoreCinemas,
      s.isFilterMode,
      s.setIsFetching,
      s.setFirstEnabledDay,
      s.setIsFiltersDrawerOpen,
      s.setIsOfferApplied,
      s.setIsFilterMode,
      s.setIsThereAtLeastOneAvailableShowTime,
      s.setMovieHasOffers,
      s.setHasOfferError,
      s.setHasOfferLoading,
    ],
    shallow
  );

  const offerId = router.query.offerId as string;

  const verifiedOffer = useVerifiedOffer(s => s.verifiedOffer, shallow);

  const { getBankOfferParams } = useBankOfferParams();
  const { getUnipalOfferParams } = useUnipalOfferParams();

  const _isOfferApplied = Boolean(offerId && verifiedOffer?.id === offerId);

  const { data: _offer } = useGetOfferDetails(
    {
      query: {
        offerId,
      },
      params: {
        cityId,
      },
    },
    {
      enabled: _isOfferApplied && !!cityId && !!offerId,
    }
  );

  useEffect(() => {
    setOffer(_offer);
  }, [_offer]);

  const { handleDeleteOffer } = useOfferHandler(offer?.offer);

  useGetMovieDetailsBySlug(
    {
      query: { slug: router.query.slug as string },
      params: {
        cityId: !isNaN(cityId) ? cityId : undefined,
        ...(_isOfferApplied && {
          offerId,
        }),
        ...getBankOfferParams(),
        unipalStudentId: getUnipalOfferParams()?.unipalStudentId,
      },
    },
    {
      // !!cityId: the films API rejects requests without a cityId, so hold
      // this refetch until the visitor has a city (server-rendered data shows
      // meanwhile); it fires as soon as a city is picked in the dialog
      enabled: !!router.query.slug && !!cityId,
      onSuccess: data => setMovie(data),
    }
  );

  const { data: _movieCinemasFilter, isFetching: isFetchingCinemas } =
    useGetMovieCinemasFilter(
      {
        params: {
          filmId: movie?.film.id,
          date: showTimeDate,
          cityId,
          experienceIds: selectedExperiencesFilter.map(filter => filter.id),
        },
      },
      {
        enabled: !!showTimeDate && !_isOfferApplied && !!cityId && !!movie,
      }
    );

  const { data: _movieExperiencesFilter, isFetching: isFetchingExperiences } =
    useGetMovieExperiencesFilter(
      {
        params: {
          filmId: movie?.film?.id,
          date: showTimeDate,
          cityId,
          cinemaIds: selectedCinemasFilter.map(filter => filter.id),
        },
      },
      {
        enabled: !!showTimeDate && !_isOfferApplied && !!cityId && !!movie,
      }
    );

  useEffect(() => {
    setMovieCinemasFilter(_movieCinemasFilter);
  }, [_movieCinemasFilter]);
  useEffect(() => {
    setMovieExperiencesFilter(_movieExperiencesFilter);
  }, [_movieExperiencesFilter]);

  const handleSetCinemasParameters = (invalidate = false): void => {
    if (hasMoreCinemas) setShowMoreBtn(false);
    if (selectedCinemasFilter.length > 0 || selectedExperiencesFilter.length > 0) {
      setIsFilterMode(true);
      setShowMoreBtn(false);
    } else {
      setIsFilterMode(false);
    }
    if (!invalidate) return;

    if (!!showTimeDate && !!cityId && invalidate) {
      setCinemasParameters({
        cityId,
        cinemaIds: isFilterMode
          ? selectedCinemasFilter.map(filter => filter.id)
          : selectedCinemas.filter(id => id !== 0),
        filterCinemaIds: selectedCinemasFilter.map(filter => filter.id),
        filterExperienceIds: selectedExperiencesFilter.map(filter => filter.id),
        showMore: hasMoreCinemas,
        date: showTimeDate,
        ...(_isOfferApplied && {
          offerId,
        }),
      });
    }
  };

  const checkedMovieOffers = useCheckMovieOffers(
    {
      params: {
        cityId,
        filmId: movie?.film?.id,
        date: showTimeDate,
      },
    },
    {
      enabled: !!cityId && !!movie?.film.id && !!showTimeDate && !_isOfferApplied,
      onSuccess: data => {
        setMovieHasOffers(data?.hasOffer);
        setHasOfferError(false);
      },
      onError: () => {
        setHasOfferError(true);
      },
    }
  );
  const hasOffer = checkedMovieOffers?.data?.hasOffer;

  const { isFetching } = useGetMovieCinemas(
    {
      query: { id: movie?.film.id },
      params: {
        ...cinemasParameters,
        ...getBankOfferParams(),
        unipalStudentId: getUnipalOfferParams()?.unipalStudentId,
      },
    },
    {
      enabled: !!cinemasParameters && !!movie,
      onSuccess: (data: filmCinemasResponse) => {
        setFilmCinemas(prevData => {
          if (isFilterMode)
            return {
              cinemas: data.cinemas,
              hasOffers: hasOffer,
            };
          else {
            if (data) {
              if (prevData && hasMoreCinemas) {
                return {
                  cinemas: [...prevData.cinemas, ...data.cinemas],
                  hasOffers: hasOffer,
                };
              } else {
                return { cinemas: data.cinemas, hasOffers: hasOffer };
              }
            }
          }
        });
      },
    }
  );

  const handleMoviePageDeleteOffer = () => {
    handleDeleteOffer();
    setDialog({});
    router.replace(`/movies/${movie.film.uniqueName}?cityId=${cityId}`);
  };

  useEffect(() => {
    setIsFetchingCinemas(isFetchingCinemas);
    setIsFetchingExperiences(isFetchingExperiences);
    setIsFetching(isFetching);
    setHasOfferLoading(checkedMovieOffers.isFetching);
  }, [
    isFetchingCinemas,
    isFetchingExperiences,
    isFetching,
    checkedMovieOffers.isFetching,
  ]);

  const handleLoadTheLeftCinemas = (): void => {
    setShowMoreBtn(false);
    setHasMoreCinemas(true);
  };

  const buildSelectedFilters = (
    cinemasFilter: selectedFilters[],
    experiencesFilter: selectedFilters[]
  ): void => {
    const mergedFilters: selectedFilters[] = [];
    movieCinemasFilter &&
      cinemasFilter.forEach(({ id, type }) =>
        mergedFilters.push({
          id,
          type,
          label: movieCinemasFilter.cinemas.find(cinema => cinema.id === id).name,
        })
      );
    movieExperiencesFilter &&
      experiencesFilter.forEach(({ id, type }) =>
        mergedFilters.push({
          id,
          type,
          label: movieExperiencesFilter.experiences.find(
            experience => experience.id === id
          ).name,
        })
      );
    setSelectedFilters(mergedFilters);
  };

  const removeSelectedFilter = ({ id, type }: selectedFilters): void => {
    setSelectedFilters(
      selectedFilters.filter(filter => filter.id !== id && filter.type !== type)
    );
    const filterType = selectedFilters.find(
      filter => filter.id === id && filter.type === type
    ).type;

    if (filterType === 'cinema')
      setSelectedCinemasFilter(selectedCinemasFilter.filter(filter => filter.id !== id));
    if (filterType === 'experience')
      setSelectedExperiencesFilter(
        selectedExperiencesFilter.filter(filter => filter.id !== id)
      );
    // setIsSeeResultsDisabled(false);
  };

  const handleSubmitFilters = (): void => {
    setIsSeeResultsDisabled(true);
    handleSetCinemasParameters(true);
  };

  const handleClearFilters = (): void => {
    setSelectedFilters([]);
    setSelectedCinemasFilter([]);
    setSelectedExperiencesFilter([]);
    setHasMoreCinemas(false);
  };

  const isThereAtLeastOneAvailableShowTime = (): void => {
    if (showTimeDate) {
      const isDisabled = movie.sessionsDates.find(
        session => session.date === showTimeDate
      )?.isDisabled;
      if (!isDisabled) setIsThereAtLeastOneAvailableShowTime(true);
      else setIsThereAtLeastOneAvailableShowTime(false);
    } else setIsThereAtLeastOneAvailableShowTime(firstEnabledDay?.isDisabled || false);
  };

  const invalidateCinemas = (): void => {
    queryClient.invalidateQueries([
      {
        method: 'get',
        url: 'films/:id/cinemas',
        query: {
          id: movie?.film.id,
        },
        params: cinemasParameters,
      },
    ]);
  };

  const invalidateMovie = (): void => {
    queryClient.invalidateQueries([
      {
        method: 'get',
        url: 'films/unique-name/:slug',
        query: {
          slug: router.query.slug,
        },
        params: {
          cityId,
        },
      },
    ]);
  };

  const callOnFilmHasBeenStarted = (): void => {
    // invalidate movie
    invalidateMovie();
    // invalidate movie cinemas
    invalidateCinemas();
  };

  const handleClickShowtime = _isOfferApplied
    ? session => {
        clearBooking();
        setDialog({
          wrapper: true,
          isRulesOfEntryDialog: true,
          rulesOfEntryDialogPayload: {
            offerId: offerId,
            // offerId: Number(offerId),
            ...session,
          },
        });
      }
    : null;

  /* A useEffect hook that is being used to reset the state of the component. */
  useEffect(() => {
    setMovie(serverMovie);
    setMovieCinemasFilter(serverMovieCinemasFilter);
    setMovieExperiencesFilter(serverMovieExperiencesFilter);

    return () => {
      setMovie(null);
      setOffer(null);
      setShowTimeDate(null);
      setFilmCinemas(null);
      setSelectedFilters([]);
      setSelectedCinemasFilter([]);
      setSelectedExperiencesFilter([]);
      setCinemasParameters(null);
      setMovieCinemasFilter(null);
      setMovieExperiencesFilter(null);
      setIsFetchingCinemas(null);
      setIsFetchingExperiences(null);
      setIsSeeResultsDisabled(null);
      setShowMoreBtn(null);
      setHasMoreCinemas(null);
      setIsFetching(null);
      setFirstEnabledDay(null);
      setIsFiltersDrawerOpen(null);
      setIsOfferApplied(null);
      setIsFilterMode(null);
      setIsThereAtLeastOneAvailableShowTime(null);
    };
  }, []);

  useEffect(() => {
    const invalidate = !(
      selectedExperiencesFilter.length > 0 || selectedCinemasFilter.length > 0
    );
    handleSetCinemasParameters(invalidate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasMoreCinemas,
    cityId,
    _isOfferApplied,
    movie,
    selectedExperiencesFilter,
    selectedCinemasFilter,
  ]);

  useEffect(() => {
    setIsSeeResultsDisabled(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExperiencesFilter, selectedCinemasFilter]);

  useEffect(() => {
    if (firstEnabledDay) {
      setShowTimeDate(firstEnabledDay.date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstEnabledDay]);

  useEffect(() => {
    invalidateCinemas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cinemasParameters]);

  useEffect(() => {
    handleSetCinemasParameters(true);
    setHasMoreCinemas(false);
    setShowMoreBtn(true);
  }, [showTimeDate]);

  useEffect(() => {
    if (movie) isThereAtLeastOneAvailableShowTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movie, showTimeDate]);

  useEffect(() => {
    buildSelectedFilters(selectedCinemasFilter, selectedExperiencesFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedCinemasFilter,
    selectedExperiencesFilter,
    movieCinemasFilter,
    movieExperiencesFilter,
  ]);

  useEffect(() => {
    setIsOfferApplied(_isOfferApplied);
  }, [_isOfferApplied]);

  return {
    clientMovie: movie,
    refetch: invalidateMovie,
    handleSubmitFilters,
    offerId,
    handleMoviePageDeleteOffer,
    handleClickShowtime,
    handleClearFilters,
    handleLoadTheLeftCinemas,
    callOnFilmHasBeenStarted,
    removeSelectedFilter,
    setFirstEnabledDay,
    setIsFiltersDrawerOpen,
    setShowTimeDate,
  };
};
